import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { ChangeEvent, Fragment } from "react";

import { ModelState } from "../../../interfaces/modelState";
import { Tooltip } from "../../tooltip/Tooltip";
import { hasValidationMessage } from "../../validationMessage/hasValidationMessage";
import { ValidationMessage } from "../../validationMessage/ValidationMessage";
import { HelperText } from "../helperText/HelperText";
import { FormLabel } from "../label/FormLabel";
import unobtrusiveValidation from "../unobtrusiveValidation";
import useFieldData from "../useFieldData";
import useFieldName from "../useFieldName";
import { Validators } from "../validator/validators";
import styles from "./RadioButtons.module.scss";

export interface Props<T> {
  id?: string;
  name: string;
  fieldName: string;
  validators?: Validators<T>;
  propertyName: string;
  modelState?: ModelState;
  setModelState?: (modelState: ModelState) => void;
  required?: boolean;
  options: { label: string; value: T; disabled?: boolean }[];
  selectedValue: T;
  fieldData: Record<string, Field<string>>;
  onChange: (value: T) => void;
  disabled?: boolean;
  direction: "column" | "row";
}

export const RadioButtons = <T extends number | string>(props: Props<T>) => {
  const { name, fieldName } = useFieldName(props.name, props.fieldName);
  const { heading, info, tooltip } = useFieldData(props.fieldData, name);
  const modelState = props.modelState;

  const onFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const fieldValue = e.target.value as T;
    if (props.onChange) {
      props.onChange(fieldValue);
    }

    if (props.setModelState) {
      props.setModelState(unobtrusiveValidation(props.validators, modelState, props.propertyName, fieldValue));
    }
  };

  const onFieldBlur = () => {
    if (props.setModelState) {
      props.setModelState(unobtrusiveValidation(props.validators, modelState, props.propertyName, props.selectedValue));
    }
  };

  const id = props.id || fieldName;
  const labelId = `${id}-label`;
  const hasError = hasValidationMessage(modelState, props.propertyName);
  const errorId = `${id}-error`;

  return (
    <div className={styles.radioButton__wrapper}>
      <FormLabel labelId={labelId} fieldName={fieldName} tooltip={tooltip.value} htmlFor={id} label={heading.value} />
      <div
        id={id}
        role="radiogroup"
        aria-labelledby={labelId}
        aria-required={props.required}
        aria-invalid={hasError}
        aria-errormessage={hasError ? errorId : undefined}
        className={`${styles.radioButton__container} ${
          props.direction === "column" ? styles["radioButton__container--column"] : ""
        }`}
        onBlur={onFieldBlur}
      >
        {props.options.map(({ disabled, label, value }) => {
          const radioBtnFieldName = `${props.id || fieldName}_${value}`;
          const tooltipFieldName = `Tooltip${radioBtnFieldName.replace(/\d+/g, "")}`;
          const radioBtnTooltip = props.fieldData[tooltipFieldName]?.value;

          return (
            <Fragment key={radioBtnFieldName}>
              <input
                id={radioBtnFieldName}
                name={fieldName}
                type="radio"
                value={value}
                className={styles.radioButton__input}
                required={props.required}
                disabled={disabled || props.disabled || false}
                onChange={onFieldChange}
                checked={props.selectedValue === value}
                autoComplete="off"
              />
              <label htmlFor={radioBtnFieldName} className="radio">
                {label}
                <Tooltip fieldName={radioBtnFieldName} value={radioBtnTooltip} />
              </label>
            </Fragment>
          );
        })}
      </div>
      <HelperText text={info.value} />
      <ValidationMessage modelState={modelState} property={props.propertyName} id={errorId} />
    </div>
  );
};
